/**
 * 指标库管理页面
 * 独立的全屏指标管理
 */

import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Popconfirm, message, Select, Input, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { KPIDimension, kpiDimensionService } from '../../../services/administrationService';
import DimensionEditModal from './DimensionEditModal';

const CATEGORY_MAP: Record<string, string> = {
    'FINANCE': '财务/业绩',
    'OPERATION': '运营/销售',
    'SUPPLY_CHAIN': '供应链/物流',
    'MARKETING': '市场/推广',
    'PRODUCT': '产品/研发',
    'DESIGN': '设计/视觉',
    'CUSTOMER_SERVICE': '客服/体验',
    'MANAGEMENT': '综合管理',
};

const DimensionLibrary: React.FC = () => {
    const [dimensions, setDimensions] = useState<KPIDimension[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    // 编辑相关
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingDim, setEditingDim] = useState<KPIDimension | null>(null);

    const loadDimensions = async () => {
        setLoading(true);
        try {
            const data = await kpiDimensionService.list();
            setDimensions(data);
        } catch (error) {
            message.error('加载指标失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDimensions();
    }, []);

    const handleAdd = () => {
        setEditingDim(null);
        setEditModalVisible(true);
    };

    const handleEdit = (record: KPIDimension) => {
        setEditingDim(record);
        setEditModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await kpiDimensionService.delete(id);
            message.success('删除成功');
            loadDimensions();
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleEditSuccess = () => {
        setEditModalVisible(false);
        loadDimensions();
    };

    // 过滤数据
    const filteredData = dimensions.filter(d => {
        const matchSearch = (d.name.toLowerCase().includes(searchText.toLowerCase()) ||
            d.code.toLowerCase().includes(searchText.toLowerCase()));
        const matchCategory = categoryFilter ? d.category === categoryFilter : true;
        return matchSearch && matchCategory;
    });

    const columns: ColumnsType<KPIDimension> = [
        {
            title: '分类',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            render: (v) => v ? <Tag color="blue">{CATEGORY_MAP[v] || v}</Tag> : '-'
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                    <span style={{ fontSize: '12px', color: '#888' }}>{record.code}</span>
                </Space>
            )
        },
        {
            title: '说明',
            dataIndex: 'description',
            key: 'description',
            ellipsis: {
                showTitle: false,
            },
            render: (description) => (
                <Tooltip placement="topLeft" title={description}>
                    {description || '-'}
                </Tooltip>
            ),
        },
        { title: '上限分数', dataIndex: 'max_score', key: 'max_score', width: 100 },
        {
            title: '越高越好',
            dataIndex: 'is_higher_better',
            key: 'higher_better',
            width: 100,
            render: v => v ? <Tag color="green">是</Tag> : <Tag color="orange">否</Tag>
        },
        {
            title: '状态',
            dataIndex: 'is_active',
            key: 'active',
            width: 100,
            render: v => v ? <Tag color="success">启用</Tag> : <Tag color="error">禁用</Tag>
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card
                title="KPI 指标库管理"
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增指标</Button>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Space>
                        <Select
                            placeholder="按分类筛选"
                            allowClear
                            style={{ width: 160 }}
                            onChange={setCategoryFilter}
                        >
                            {Object.entries(CATEGORY_MAP).map(([key, label]) => (
                                <Select.Option key={key} value={key}>{label}</Select.Option>
                            ))}
                        </Select>
                        <Input
                            placeholder="搜索名称或代码"
                            prefix={<SearchOutlined />}
                            style={{ width: 200 }}
                            onChange={e => setSearchText(e.target.value)}
                        />
                        <Button icon={<ReloadOutlined />} onClick={loadDimensions}>刷新</Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    size="middle"
                    bordered
                />
            </Card>

            <DimensionEditModal
                visible={editModalVisible}
                editingDim={editingDim}
                onCancel={() => setEditModalVisible(false)}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

export default DimensionLibrary;
